import * as React from 'react';

import { enumSidecarStatus } from '@rematter/frontend-lib-api-client';
import { DeviceType } from '@rematter/frontend-lib-api-client/grpc/sidecar/v1/local_api_pb';
import {
  Badge,
  Button,
  ConfirmPopoverButton,
  DialogClosingConfirmation,
  EntityDetails,
  LocalizedDate,
  Menu,
  ThemeColor,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  ArrowLeft,
  LucideCamera,
  LucideDownload,
  LucideEllipsis,
  LucidePrinter,
  LucideRefreshCw,
  LucideScanLine,
  LucideWeight,
  Trash2,
} from 'lucide-react';

import { sidecarDeleteAtom } from '../../../_graphql/mutationSidecarDelete';
import { sidecarTriggerUpdateCheckAtom } from '../../../_graphql/mutationSidecarTriggerUpdateCheck';
import { sidecarUpdateAtom } from '../../../_graphql/mutationSidecarUpdate';
import { useSidecarEventsSubscription } from '../../../_graphql/subscriptionSidecarEvents';
import { useSettingsLocation } from '../../../_state/settingsModule.state';
import * as state from '../../../_state/sidecarDetail.state';
import {
  $discardBaseline,
  $hasUnsavedChanges,
  $resetSidecarFormToSidecar,
  SidecarFormState,
} from '../../../_state/sidecarForm.state';
import { usePopulateSidecarForm } from '../../../hooks/usePopulateSidecarForm';
import {
  isVersionOutdated,
  useSidecarLatestVersion,
} from '../../../hooks/useSidecarLatestVersion';
import { useTranslations } from '../../../i18n/useTranslations';
import type { SidecarDisplayStatus } from '../../../types/sidecar';

import { SidecarTabDetails } from './SidecarTabDetails';
import { SidecarTabLogs } from './SidecarTabLogs';
import { SidecarTabUploadQueue } from './SidecarTabUploadQueue';

import { useHardwareSidecarConnection } from '~/shared/providers/libp2p/useHardwareSidecarConnection';
import { useSidecarClient } from '~/shared/providers/libp2p/useSidecarClient';
import { useHardwarePresenceMembers } from '~/shared/providers/presence';

const STATUS_COLORS: Record<SidecarDisplayStatus, ThemeColor> = {
  Pending: 'warning',
  Online: 'success',
  Offline: 'neutral',
};

type SidecarDetailTab = 'details' | 'logs' | 'uploadQueue';

export const SidecarDetailView = () => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();

  const [currentTab, setCurrentTab] =
    React.useState<SidecarDetailTab>('details');
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [isCheckingUpdate, setIsCheckingUpdate] = React.useState(false);
  const [pendingTabChange, setPendingTabChange] =
    React.useState<SidecarDetailTab | null>(null);

  const sidecarId = useAtomValue(state.$sidecarId);
  const { data: sidecar, refetch } = useAtomValue(state.$remoteSidecarDetail);
  const isLoading = useAtomValue(state.$isSidecarLoading);
  const error = useAtomValue(state.$sidecarError);
  const hasUnsavedChanges = useAtomValue($hasUnsavedChanges);

  const [, resetForm] = useAtom($resetSidecarFormToSidecar);
  const [discardBaseline, setDiscardBaseline] = useAtom($discardBaseline);

  const deleteSidecar = useSetAtom(sidecarDeleteAtom({}));
  const updateSidecar = useSetAtom(sidecarUpdateAtom({}));
  const triggerUpdateCheck = useSetAtom(sidecarTriggerUpdateCheckAtom({}));

  const presenceMembers = useHardwarePresenceMembers();

  const toast = useToast();
  const { latestVersion, downloadUrl } = useSidecarLatestVersion();
  usePopulateSidecarForm({ sidecar });

  const displayStatus: SidecarDisplayStatus | undefined = sidecar
    ? sidecar.status === enumSidecarStatus.Pending
      ? 'Pending'
      : presenceMembers.has(sidecar.id)
        ? 'Online'
        : 'Offline'
    : undefined;

  const { connection: sidecarConnection, isConnecting } =
    useHardwareSidecarConnection({
      sidecar: sidecar
        ? {
            id: sidecar.id,
            peerId: sidecar.peerId,
            multiaddrs: sidecar.multiaddrs,
            status: sidecar.status,
          }
        : null,
      onConnectionFailed: refetch,
    });

  const isOnline = sidecar ? presenceMembers.has(sidecar.id) : false;
  const sidecarClient = useSidecarClient(sidecar?.id ?? '');

  useSidecarEventsSubscription(undefined, {
    onEvent: (event) => {
      if (event?.id === sidecarId) {
        refetch?.();
      }
    },
  });

  const handleCheckForUpdate = React.useCallback(async () => {
    if (!sidecarId) return;
    try {
      setIsCheckingUpdate(true);
      await triggerUpdateCheck({ id: sidecarId });
      toast.success(
        t.hardwareView.sidecarDetailView.toast.updateCheckTriggered,
      );
    } catch (err) {
      toast.error(
        t.hardwareView.sidecarDetailView.toast.updateCheckError(
          err instanceof Error
            ? err.message
            : t.hardwareView.common.unknownError,
        ),
      );
    } finally {
      setIsCheckingUpdate(false);
    }
  }, [sidecarId, triggerUpdateCheck, toast, t]);

  const handleSync = React.useCallback(
    async (deviceType: number) => {
      if (!sidecarClient) return;
      try {
        for await (const update of sidecarClient.runDiscovery({
          deviceType,
        })) {
          if (update.phase === 'starting') {
            toast.info(update.message);
          } else if (update.phase === 'completed') {
            toast.success(update.message);
          }
        }
        refetch?.();
      } catch (err) {
        toast.error(
          t.hardwareView.sidecarDetailView.toast.syncFailed(
            err instanceof Error
              ? err.message
              : t.hardwareView.common.unknownError,
          ),
        );
      }
    },
    [sidecarClient, toast, t, refetch],
  );

  const handleBack = React.useCallback(() => {
    setLocation('/hardware');
  }, [setLocation]);

  const handleDiscard = React.useCallback(() => {
    resetForm();
    toast.success(t.hardwareView.sidecarDetailView.toast.discarded);
  }, [resetForm, toast, t]);

  const handleSaveUpdates = React.useCallback(
    async (data?: SidecarFormState) => {
      if (!sidecarId || !data) return;

      try {
        setIsUpdating(true);
        await updateSidecar({
          id: sidecarId,
          name: data.name.trim(),
          operatingSystem: data.operatingSystem,
          operatingSystemVersion: data.operatingSystemVersion.trim(),
          facilityId: data.facilityId || null,
          discoveryEnabled: data.discoveryEnabled,
        });

        setDiscardBaseline(data);
        toast.success(t.hardwareView.sidecarDetailView.toast.updated);
        refetch?.();
      } catch (err) {
        toast.error(
          t.hardwareView.sidecarDetailView.toast.updateError(
            err instanceof Error
              ? err.message
              : t.hardwareView.common.unknownError,
          ),
        );
      } finally {
        setIsUpdating(false);
      }
    },
    [sidecarId, updateSidecar, setDiscardBaseline, toast, t, refetch],
  );

  const handleBeforeChangeTab = React.useCallback(
    (newTab: string) => {
      if (currentTab === 'details' && hasUnsavedChanges) {
        setPendingTabChange(newTab as SidecarDetailTab);
        return false;
      }
      return true;
    },
    [currentTab, hasUnsavedChanges],
  );

  const handleConfirmTabChange = React.useCallback(() => {
    if (pendingTabChange && discardBaseline) {
      resetForm();
      setCurrentTab(pendingTabChange);
    }
    setPendingTabChange(null);
  }, [pendingTabChange, discardBaseline, resetForm]);

  const handleCancelTabChange = React.useCallback(() => {
    setPendingTabChange(null);
  }, []);

  const handleDelete = React.useCallback(async () => {
    if (!sidecarId) return;

    try {
      await deleteSidecar({ id: sidecarId });
      toast.success(t.hardwareView.sidecarDetailView.messages.deleteSuccess);
      setLocation('/hardware');
    } catch (err) {
      toast.error(
        t.hardwareView.sidecarDetailView.messages.deleteError(
          err instanceof Error
            ? err.message
            : t.hardwareView.common.unknownError,
        ),
      );
      throw err;
    }
  }, [sidecarId, deleteSidecar, toast, setLocation, t]);

  const showDownloadLatest = isVersionOutdated(sidecar?.version, latestVersion);

  const headerActions = React.useMemo(() => {
    if (!sidecar) return null;
    return (
      <div className="flex items-center gap-2">
        {showDownloadLatest && downloadUrl && latestVersion && (
          <Button
            variant="solid"
            icon={<LucideDownload className="size-4" />}
            onPress={() => window.open(downloadUrl, '_blank')}
          >
            {t.hardwareView.sidecarDetailView.actions.downloadLatest(
              latestVersion,
            )}
          </Button>
        )}
        <ConfirmPopoverButton
          onConfirm={handleDelete}
          message={t.hardwareView.sidecarDetailView.messages.deleteConfirmation}
          confirmLabel={t.hardwareView.common.confirmDelete}
          cancelLabel={t.hardwareView.common.cancel}
          buttonProps={{
            variant: 'outline',
            color: 'failure',
            icon: <Trash2 />,
          }}
        >
          {t.hardwareView.sidecarDetailView.actions.delete}
        </ConfirmPopoverButton>
        <Menu
          trigger={
            <Button
              variant="outline"
              size="icon"
              className="size-10"
              icon={<LucideEllipsis size={18} />}
            />
          }
          items={[
            {
              label: t.hardwareView.sidecarDetailView.actions.checkForUpdate,
              icon: <LucideRefreshCw />,
              onAction: handleCheckForUpdate,
              isDisabled: isCheckingUpdate || !isOnline,
            },
            'separator',
            {
              label: t.hardwareView.sidecarDetailView.actions.syncCameras,
              icon: <LucideCamera />,
              onAction: () => handleSync(DeviceType.CAMERAS),
              isDisabled: !isOnline,
            },
            {
              label: t.hardwareView.sidecarDetailView.actions.syncScanners,
              icon: <LucideScanLine />,
              onAction: () => handleSync(DeviceType.SCANNERS),
              isDisabled: !isOnline,
            },
            {
              label: t.hardwareView.sidecarDetailView.actions.syncPrinters,
              icon: <LucidePrinter />,
              onAction: () => handleSync(DeviceType.PRINTERS),
              isDisabled: !isOnline,
            },
            {
              label: t.hardwareView.sidecarDetailView.actions.syncScales,
              icon: <LucideWeight />,
              onAction: () => handleSync(DeviceType.SCALES),
              isDisabled: !isOnline,
            },
          ]}
        />
      </div>
    );
  }, [
    sidecar,
    showDownloadLatest,
    downloadUrl,
    latestVersion,
    handleCheckForUpdate,
    isCheckingUpdate,
    isOnline,
    handleSync,
    handleDelete,
    t,
  ]);

  if (isLoading && !sidecar) {
    return (
      <div className="@SidecarDetailView flex h-full flex-col">
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 w-1/4 rounded bg-neutral/20"></div>
            <div className="h-8 w-1/3 rounded bg-neutral/20"></div>
            <div className="h-32 rounded bg-neutral/20"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !sidecar) {
    return (
      <div className="@SidecarDetailView flex h-full flex-col p-6">
        <div className="text-failure">
          {t.hardwareView.common.errorLabel}:{' '}
          {error?.message ??
            t.hardwareView.sidecarDetailView.messages.sidecarNotFound}
        </div>
        <Button variant="outline" onPress={handleBack} className="mt-4 w-fit">
          <ArrowLeft className="mr-2 size-4" />
          {t.hardwareView.sidecarDetailView.actions.backToHardware}
        </Button>
      </div>
    );
  }

  return (
    <div className="@SidecarDetailView flex h-full flex-col">
      <EntityDetails
        className="relative"
        onChangeTab={(tab) => setCurrentTab(tab as SidecarDetailTab)}
        onBeforeChangeTab={handleBeforeChangeTab}
        currentTab={currentTab}
        onBackPressed={handleBack}
        primaryTitle={sidecar.name}
        subtitle={
          <div className="flex items-center gap-2">
            {displayStatus && (
              <Badge color={STATUS_COLORS[displayStatus]}>
                {t.hardwareView.sidecarsPanel.statusLabels[displayStatus]}
              </Badge>
            )}
            {isConnecting && (
              <Badge color="warning">
                {t.hardwareView.sidecarDetailView.connectionLabels.connecting}
              </Badge>
            )}
            {sidecarConnection?.connected && (
              <Badge
                color={
                  sidecarConnection.type === 'direct' ? 'primary' : 'neutral'
                }
              >
                {sidecarConnection.type === 'direct'
                  ? t.hardwareView.sidecarDetailView.connectionLabels.direct
                  : t.hardwareView.sidecarDetailView.connectionLabels.relayed}
              </Badge>
            )}
            <span>
              {sidecar.operatingSystem} {sidecar.operatingSystemVersion}
            </span>
            {sidecar.version && (
              <span className="text-neutral/60">v{sidecar.version}</span>
            )}
            <span className="text-neutral/60">
              • {t.hardwareView.sidecarDetailView.fields.created}{' '}
              <LocalizedDate date={sidecar.timestamps.createdAt} />
            </span>
          </div>
        }
        headerActions={headerActions}
        tabs={{
          details: {
            title: t.hardwareView.sidecarDetailView.tabs.details,
            content: () => (
              <SidecarTabDetails
                sidecar={sidecar}
                onDiscard={handleDiscard}
                onSaveUpdates={handleSaveUpdates}
                isSaving={isUpdating}
              />
            ),
          },
          logs: {
            title: t.hardwareView.sidecarDetailView.tabs.logs,
            content: () => <SidecarTabLogs sidecarId={sidecar.id} />,
          },
          uploadQueue: {
            title: t.hardwareView.sidecarDetailView.tabs.uploadQueue,
            content: () => <SidecarTabUploadQueue sidecarId={sidecar.id} />,
          },
        }}
      />
      <DialogClosingConfirmation
        isOpen={!!pendingTabChange}
        message={t.hardwareView.sidecarDetailView.messages.unsavedDataMessage}
        onCancel={handleCancelTabChange}
        onDiscard={handleConfirmTabChange}
      />
    </div>
  );
};
